import type { MfaCreateConfigDto } from '@terra/shared-contracts-applications';
import type {
  BodySignatureAlgorithmType,
  BodySignatureConfiguration,
} from '@terra/shared-schemas-orchestrator';
import { create } from 'zustand';

export type CustomRoleDraft = {
  name: string;
  description: string;
};

export type CredentialLoginType = 'username' | 'email';

export enum AddCredentialsDialogStep {
  BASIC_INFORMATION = 0,
  LOGIN_INFORMATION = 1,
  REQUEST_AUTHENTICATION = 2,
}

export type ValidationErrors = {
  email: string;
  role: string;
  loginUrl: string;
  basicAuthUsername: string;
  basicAuthPassword: string;
  mtlsOrigin: string;
  mtlsCertificate: string;
  mtlsKey: string;
  totpSecret: string;
  authentication: string;
};

export type KeyValuePair = {
  key: string;
  value: string;
};

export type CredentialCookie = {
  name: string;
  value: string;
  path: string | null;
  domain: string | null;
  expires: string | null;
  httpOnly: boolean | null;
  secure: boolean | null;
  sameSite: string | null;
  priority: string | null;
};

export type CredentialData = {
  id: string;
  name: string | null;
  ssoProvider: string;
  username: string;
  role: string;
  roleId?: string | null;
  roleDescription?: string | null;
  loginUrl?: string | null;
  password: string;
  basicAuthUsername: string;
  basicAuthPassword: string;
  mtlsCert: string;
  mtlsKey: string;
  mtlsPfx: string;
  mtlsOrigin: string;
  mtlsPassphrase: string;
  tenant: string | null;
  headers: { key: string; value: string }[];
  cookies: CredentialCookie[];
  secrets: { key: string; value: string }[];
  bodySignature?: BodySignatureConfiguration | null;
  mfaConfig: MfaCreateConfigDto[];
  generatedMailboxAddress: string;
};

export interface MtlsFormMeta {
  enabled: boolean;
  certFileName: string;
  keyFileName: string;
  pfxFileName: string;
}

export interface RequestSigningFormMeta {
  headerName: string;
  signingSecret: string;
  algorithm: BodySignatureAlgorithmType;
}

export interface MfaFormConfig {
  id?: string;
  enabled: boolean;
  providerType: 'EMAIL' | 'SMS' | 'OTP' | 'WEBHOOK';
  totpSecret?: string;
}

export interface FormMeta {
  loginType: CredentialLoginType;
  email: string;
  customRoles: CustomRoleDraft[];
  requiresBasicAuth: boolean;
  failedLoginAttemptLimit: string;
  mfaEnabled: boolean;
  mfaConfigs: MfaFormConfig[];
  mtls: MtlsFormMeta;
  requestSigning: RequestSigningFormMeta;
  secretKeys: KeyValuePair[];
}

interface AddCredentialsDialogState {
  currentStep: AddCredentialsDialogStep;
  credential: CredentialData;
  formMeta: FormMeta;
  validationErrors: ValidationErrors;
  visitedSteps: Set<AddCredentialsDialogStep>;
}

interface AddCredentialsDialogActions {
  setCurrentStep: (step: AddCredentialsDialogStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  setCredential: (values: Partial<CredentialData>) => void;
  setFormMeta: (values: Partial<FormMeta>) => void;
  setValidationErrors: (errors: Partial<ValidationErrors>) => void;
  setVisitedSteps: (step: AddCredentialsDialogStep) => void;
  reset: () => void;
  initializeFromCredential: (
    credential: CredentialData,
    formMeta?: Partial<FormMeta>,
  ) => void;
}

export interface AddCredentialsDialogStore
  extends AddCredentialsDialogState,
    AddCredentialsDialogActions {}

export const createEmptyCredentialCookie = (): CredentialCookie => ({
  name: '',
  value: '',
  path: null,
  domain: null,
  expires: null,
  httpOnly: null,
  secure: null,
  sameSite: null,
  priority: null,
});

const initialCredential: CredentialData = {
  id: '',
  name: null,
  ssoProvider: 'NONE',
  username: '',
  role: '',
  roleId: null,
  roleDescription: null,
  loginUrl: null,
  password: '',
  basicAuthUsername: '',
  basicAuthPassword: '',
  mtlsCert: '',
  mtlsKey: '',
  mtlsPfx: '',
  mtlsOrigin: '',
  mtlsPassphrase: '',
  tenant: null,
  headers: [{ key: '', value: '' }],
  cookies: [createEmptyCredentialCookie()],
  secrets: [],
  bodySignature: null,
  mfaConfig: [],
  generatedMailboxAddress: '',
};

const initialFormMeta: FormMeta = {
  loginType: 'username',
  email: '',
  customRoles: [],
  requiresBasicAuth: false,
  failedLoginAttemptLimit: '',
  mfaEnabled: false,
  mfaConfigs: [],
  mtls: {
    enabled: false,
    certFileName: '',
    keyFileName: '',
    pfxFileName: '',
  },
  requestSigning: {
    headerName: '',
    signingSecret: '',
    algorithm: 'sha256',
  },
  secretKeys: [{ key: '', value: '' }],
};

const initialValidationErrors: ValidationErrors = {
  email: '',
  role: '',
  loginUrl: '',
  basicAuthUsername: '',
  basicAuthPassword: '',
  mtlsOrigin: '',
  mtlsCertificate: '',
  mtlsKey: '',
  totpSecret: '',
  authentication: '',
};

const initialState: AddCredentialsDialogState = {
  currentStep: AddCredentialsDialogStep.BASIC_INFORMATION,
  credential: initialCredential,
  formMeta: initialFormMeta,
  validationErrors: initialValidationErrors,
  visitedSteps: new Set([AddCredentialsDialogStep.BASIC_INFORMATION]),
};

export const useAddCredentialsDialogStore = create<AddCredentialsDialogStore>(
  (set) => ({
    ...initialState,
    setCurrentStep: (step) => set({ currentStep: step }),
    goToNextStep: () =>
      set((state) => {
        const nextStep = state.currentStep + 1;

        if (nextStep > AddCredentialsDialogStep.REQUEST_AUTHENTICATION) {
          return {
            currentStep: AddCredentialsDialogStep.REQUEST_AUTHENTICATION,
          };
        }

        return {
          currentStep: nextStep,
          visitedSteps: new Set([...state.visitedSteps, nextStep]),
        };
      }),
    goToPreviousStep: () =>
      set((state) => {
        const previousStep = state.currentStep - 1;

        if (previousStep < 0) {
          return { currentStep: 0 };
        }

        return { currentStep: previousStep };
      }),
    setCredential: (values) =>
      set((state) => ({
        credential: {
          ...state.credential,
          ...values,
        },
      })),
    setFormMeta: (values) =>
      set((state) => ({
        formMeta: {
          ...state.formMeta,
          ...values,
        },
      })),
    setValidationErrors: (errors) =>
      set((state) => ({
        validationErrors: {
          ...state.validationErrors,
          ...errors,
        },
      })),
    setVisitedSteps: (step) =>
      set((state) => ({
        visitedSteps: new Set([...state.visitedSteps, step]),
      })),
    reset: () => set({ ...initialState }),
    initializeFromCredential: (credential, formMeta) =>
      set({
        credential,
        formMeta: { ...initialFormMeta, ...formMeta },
        currentStep: AddCredentialsDialogStep.BASIC_INFORMATION,
        validationErrors: initialValidationErrors,
        visitedSteps: new Set([
          AddCredentialsDialogStep.BASIC_INFORMATION,
          AddCredentialsDialogStep.LOGIN_INFORMATION,
          AddCredentialsDialogStep.REQUEST_AUTHENTICATION,
        ]),
      }),
  }),
);
